export const emptyPerson = () => ({ name: "", position: "", clientId: "" });
export const localDateOnly = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const dateOnly = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.toISOString().slice(0, 10) === `${year}-${month}-${day}` ? `${year}-${month}-${day}` : "";
};

const periodMonth = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
  may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7,
  sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10,
  dec: 11, december: 11,
};

const periodDate = (day, month, year) => {
  const monthIndex = periodMonth[String(month || "").toLowerCase()];
  if (monthIndex === undefined || !/^\d{4}$/.test(year)) return "";
  const date = new Date(Date.UTC(Number(year), monthIndex, Number(day)));
  return date.getUTCFullYear() === Number(year) && date.getUTCMonth() === monthIndex && date.getUTCDate() === Number(day)
    ? date.toISOString().slice(0, 10)
    : "";
};

// QT keeps exact start/end dates even when its displayed Period collapses to “Sep 2026”.
// Use endDate so a one-day period naturally remains that same date; fall back to Period for older records.
export const documentDateFromQuotation = (quotation = {}) => {
  const endDate = dateOnly(quotation.endDate);
  if (endDate) return endDate;

  const period = String(quotation.period || "").trim();
  const dates = [...period.matchAll(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/g)];
  if (dates.length) {
    const [, day, month, year] = dates[dates.length - 1];
    return periodDate(day, month, year) || localDateOnly();
  }

  const monthOnly = period.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthOnly) {
    const [, month, year] = monthOnly;
    const monthIndex = periodMonth[month.toLowerCase()];
    if (monthIndex !== undefined) return new Date(Date.UTC(Number(year), monthIndex + 1, 0)).toISOString().slice(0, 10);
  }

  return localDateOnly();
};

const monthValue = (date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

const monthLabel = (value) => new Intl.DateTimeFormat("th-TH", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
}).format(new Date(`${value}-01T00:00:00Z`));

// The API accepts YYYY-MM. Present a fixed, easy-to-scan 12-month window
// rather than relying on each browser's native month-picker UI.
export const completionMonthOptions = (from = new Date(), selectedValue = "") => {
  const firstMonth = new Date(Date.UTC(from.getFullYear(), from.getMonth(), 1));
  const options = Array.from({ length: 12 }, (_, index) => {
    const month = new Date(firstMonth);
    month.setUTCMonth(firstMonth.getUTCMonth() + index);
    const value = monthValue(month);
    return { value, label: monthLabel(value) };
  });

  if (selectedValue && !options.some((option) => option.value === selectedValue)) {
    options.unshift({ value: selectedValue, label: `${monthLabel(selectedValue)} (จากเอกสารเดิม)` });
  }

  return options;
};
const personToForm = (person) => person ? ({ name: person.name || "", position: person.position || "", clientId: person.company?.clientId || "" }) : emptyPerson();

export const toFormValues = (document, defaultDocumentDate = localDateOnly()) => ({
  documentDate: dateOnly(document.documentDate) || defaultDocumentDate,
  hiringClientId: document.hiringClient?.clientId || "",
  deliveredToClientId: document.deliveredToClient?.clientId || "",
  completionPercentage: String(document.completionPercentage ?? 100),
  expectedCompletionMonth: document.completionPercentage === 100 ? "" : document.expectedCompletionMonth || "",
  sender: personToForm(document.sender),
  recipients: [personToForm(document.recipients?.[0]), ...(document.additionalRecipientEnabled ? [personToForm(document.recipients?.[1])] : [])],
  additionalRecipientEnabled: Boolean(document.additionalRecipientEnabled),
  remarkEnabled: Boolean(document.remarkEnabled),
  remark: document.remarkEnabled ? document.remark || "" : "",
});

export const mergeLatestQuotationFields = (document = {}, latest = {}) => ({
  ...document,
  projectName: latest.projectName || document.projectName,
  quotationNumber: latest.quotationNumber || document.quotationNumber,
});

export const validPercentage = (value) => /^\d+(?:\.\d{1,2})?$/.test(String(value)) && Number(value) >= 0 && Number(value) <= 100;
export const remainingPercentage = (value) => validPercentage(value) ? Math.round((100 - Number(value)) * 100) / 100 : null;
export const toPayload = (values) => ({
  documentDate: values.documentDate,
  hiringClientId: values.hiringClientId,
  deliveredToClientId: values.deliveredToClientId,
  completionPercentage: Number(values.completionPercentage),
  expectedCompletionMonth: Number(values.completionPercentage) === 100 ? null : values.expectedCompletionMonth,
  sender: { ...values.sender, name: values.sender.name.trim(), position: values.sender.position.trim() },
  recipients: values.recipients.slice(0, values.additionalRecipientEnabled ? 2 : 1).map((person) => ({ ...person, name: person.name.trim(), position: person.position.trim() })),
  additionalRecipientEnabled: values.additionalRecipientEnabled,
  remarkEnabled: values.remarkEnabled,
  remark: values.remarkEnabled ? values.remark : "",
});

export const validateHandover = (values, clients) => {
  const errors = {};
  const checkClient = (key, id, required = true) => {
    if (!id && required) errors[key] = "กรุณาเลือกบริษัท";
    else if (id && !clients.some((client) => client._id === id)) errors[key] = "บริษัทนี้ไม่อยู่ในรายการแล้ว กรุณาเลือกบริษัทใหม่ก่อนบันทึก";
  };
  const date = values.documentDate;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number(date.slice(0, 4)) < 1 || Number.isNaN(Date.parse(`${date}T00:00:00Z`)) || new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date) errors.documentDate = "กรุณาเลือกวันที่หนังสือที่ถูกต้อง";
  if (!validPercentage(values.completionPercentage)) errors.completionPercentage = "กรอกตัวเลข 0–100 และทศนิยมไม่เกิน 2 หลัก";
  else if (Number(values.completionPercentage) < 100 && (!/^\d{4}-(0[1-9]|1[0-2])$/.test(values.expectedCompletionMonth) || Number(values.expectedCompletionMonth.slice(0, 4)) < 1)) errors.expectedCompletionMonth = "กรุณาเลือกเดือนและปีที่คาดว่าจะเสร็จ";
  checkClient("hiringClientId", values.hiringClientId);
  checkClient("deliveredToClientId", values.deliveredToClientId);
  const checkPerson = (key, person = emptyPerson(), { nameRequired = true, positionRequired = true, companyRequired = true } = {}) => {
    if ((nameRequired && !person.name.trim()) || person.name.length > 300) errors[`${key}.name`] = "กรุณากรอกข้อความไม่เกิน 300 ตัวอักษร";
    if (positionRequired && (!person.position.trim() || person.position.length > 300)) errors[`${key}.position`] = "กรุณากรอกข้อความไม่เกิน 300 ตัวอักษร";
    if (!positionRequired && person.position.length > 300) errors[`${key}.position`] = "กรุณากรอกข้อความไม่เกิน 300 ตัวอักษร";
    checkClient(`${key}.clientId`, person.clientId, companyRequired);
  };
  checkPerson("sender", values.sender);
  checkPerson("recipients.0", values.recipients[0], { nameRequired: false, positionRequired: false, companyRequired: false });
  if (values.additionalRecipientEnabled) checkPerson("recipients.1", values.recipients[1], { nameRequired: false, positionRequired: false, companyRequired: false });
  if (values.remarkEnabled && (!values.remark.trim() || values.remark.length > 5000)) errors.remark = "กรุณากรอกหมายเหตุไม่เกิน 5,000 ตัวอักษร";
  return errors;
};

export const thaiDate = (value, monthOnly = false) => {
  if (!value) return "…";
  const date = new Date(`${value}${monthOnly ? "-01" : ""}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "…";
  return new Intl.DateTimeFormat("th-TH", { ...(monthOnly ? {} : { day: "numeric" }), month: "long", year: "numeric", timeZone: "UTC" }).format(date);
};

export const clientCompanyName = (client) =>
  client?.companyBaseName || String(client?.customerName || "").replace(/\s*\([^)]*\)\s*$/, "").trim();

// Prefer the current client base name so branch text such as “(สำนักงานใหญ่)” is omitted.
export const companyLabel = (id, snapshot, clients) =>
  clientCompanyName(clients.find((client) => client._id === id)) || String(snapshot?.companyName || "").replace(/\s*\([^)]*\)\s*$/, "").trim() || "…";

// React PDF can clip the final Thai glyph in a signature-company line. Keep one literal
// trailing space for that rendering context without changing the stored company name.
export const signatureCompanyLabel = (id, snapshot, clients) => `${companyLabel(id, snapshot, clients)} `;
